/*
 * Memberships page
 *
 * Presents membership plans, a 3-day free trial section, required equipment,
 * hygiene & etiquette guidance, and private training details. This page is
 * mostly static content composed using 'SectionCard' and small helper
 * components.
 *
 * Notes:
 * - The component listens to the router 'location.hash' and will scroll to
 *   matching anchors (e.g. /memberships#3-day-free-trial) after a small delay
 *   to allow content to render.
 */

import React, { useEffect } from 'react';
import {
  Container, Typography, Grid, Card,
  Box, Tabs, Tab, Divider
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PersonIcon from '@mui/icons-material/Person';
import SportsMartialArtsIcon from '@mui/icons-material/SportsMartialArts';
import HygieneIcon from '@mui/icons-material/HealthAndSafety';
import SectionCard from '../components/SectionCard';
import ScrollSection from '../components/ScrollSection';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const MembershipTabs = () => {
  const [value, setValue] = React.useState(0);
  const options = [
    { label: 'Grappling', price: '$140/month', perks: ['Unlimited Grappling Classes', 'Month-to-Month'] },
    { label: 'Striking', price: '$140/month', perks: ['Unlimited Striking Classes', 'Month-to-Month'] },
    { label: 'Combined', price: '$160/month', perks: ['Unlimited Grappling + Striking Classes', 'Month-to-Month'] }
  ];

  return (
    <Box sx={{ backgroundColor: '#222', p: { xs: 1.5, sm: 4 }, borderRadius: 2, textAlign: 'left' }}>
      <Tabs
        value={value}
        onChange={(e, val) => setValue(val)}
        textColor="inherit"
        indicatorColor="secondary"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {options.map((opt, i) => (
          <Tab
            key={i}
            label={opt.label}
            sx={{
              color: '#fff',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              fontWeight: 'bold',
              // prevent label wrapping and keep tabs compact on mobile
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: 100 },
              px: { xs: 1, sm: 2 },
            }}
          />
        ))}
      </Tabs>
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" color="secondary" sx={{ fontWeight: 'bold' }}>{options[value].price}</Typography>
        {options[value].perks.map((perk, i) => (
          <Typography key={i} sx={{ fontSize: '1.1rem', mt: 1 }}>✓ {perk}</Typography>
        ))}
      </Box>
    </Box>
  );
};

const Memberships = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // Delay slightly to allow the page to render before scrolling
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [location]);
  return (
    <Container maxWidth="xl" sx={{ backgroundColor: '#111', color: '#fff', py: 6 }}>
      <Helmet>
        <title>Memberships & 3‑Day Trial — Kingdom MMA Edmonton</title>
        <meta name="description" content="Month-to-month memberships, no sign-up fees. Try a 3‑day free trial at Kingdom MMA — flexible plans for grappling, striking, and combined training." />
        <link rel="canonical" href="https://www.kingdommixedmartialarts.ca/memberships/" />
        <meta property="og:title" content="Memberships — Kingdom MMA and Fitness" />
        <meta property="og:description" content="Membership plans and trial information for Kingdom MMA and Fitness." />
        <meta property="og:url" content="https://www.kingdommixedmartialarts.ca/memberships/" />
  <meta property="og:image" content="https://www.kingdommixedmartialarts.ca/logo-no-text.svg" />
      </Helmet>
      <Typography variant="h3" align="left" sx={{ mb: 5, fontWeight: 'bold' }}>Membership Information</Typography>
      <ScrollSection id="membership-plans">
        <SectionCard
          title="Membership Plans"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/c_crop,ar_1:1/v1763685123/IMG_4735_2_q0uwdp.jpg"
          icon={<FitnessCenterIcon />}
          invert={false}
          isFirst={true}
        >
          <Typography sx={{ mb: 2 }}>
            Month-to-month memberships with no sign-up fees or long-term contracts.
            You can pause or cancel with 30 days' notice.
          </Typography>
          <MembershipTabs />
          <Typography sx={{ mt: 4 }}>
            Available payment methods: void cheque, pre-authorized debit form, or credit card
            (note: Debit Visa & Debit Mastercard not accepted).
          </Typography>
        </SectionCard>
      </ScrollSection>

      <ScrollSection id="3-day-free-trial">
        <SectionCard
          title="3-Day Trial"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763685124/IMG_4709_2_o4t4dk.jpg"
          icon={<SportsMartialArtsIcon />}
          invert={true}
        >
          <Typography>• Choose any class that matches your experience level</Typography>
          <Typography>• No booking required—just show up</Typography>
          <Typography>• Arrive 10–15 minutes early to fill out a waiver and get ready</Typography>
          <Typography>• Trial includes 3 consecutive days</Typography>
          <Divider sx={{ my: 2, backgroundColor: '#555' }} />
          <Typography>• Trial is for first-time visitors only</Typography>
          <Typography>• Participants under 18 must have a parent/guardian sign the waiver</Typography>
          <Typography>• All classes are 14+</Typography>
        </SectionCard>
      </ScrollSection>

      <ScrollSection id="required-equipment">
        <SectionCard
          title="Required Equipment for Striking"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763685127/IMG_4644_emgzke.jpg"
          icon={<FitnessCenterIcon />}
          invert={false}
        >
          <Box sx={{ display: 'grid', gap: 4 }}>
            {[
              {
                label: 'Muay Thai Kickboxing',
                trial: 'Gloves and shin guards provided',
                member: '16 oz gloves, shin guards, shorts, shirt, hand wraps (recommended), mouthguard (required for sparring)'
              },
              {
                label: 'Boxing',
                trial: 'Gloves provided',
                member: '16 oz gloves, hand wraps (recommended), mouthguard (required for sparring)'
              },
            ].map((style, i) => (
              <Card key={i} sx={{ backgroundColor: '#222', p: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>{style.label}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>🧪 Trial Gear</Typography>
                    <Typography>{style.trial}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>👤 Member Gear</Typography>
                    <Typography>{style.member}</Typography>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Box>
        </SectionCard>
      </ScrollSection>
        
      <ScrollSection id="required-equipment-grappling">
        <SectionCard
          title="Required Equipment for Grappling"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763685126/IMG_4082_5_snp2mj.jpg"
          icon={<FitnessCenterIcon />}
          invert={true}
        >
          <Box sx={{ display: 'grid', gap: 4 }}>
            {[
              {
                label: 'BJJ – No Gi',
                trial: 'Tight-fitting wear; no zippers/velcro',
                member: 'Rashguard, MMA shorts, Spats (optional), mouthguard (recommended)'
              },
              {
                label: 'BJJ – Gi',
                trial: 'Gi and indoor flip flops',
                member: 'Gi and mouthguard (recommended)'
              },
              {
                label: 'Wrestling',
                trial: 'Tight-fitting wear; no zippers/velcro',
                member: 'Rashguard, MMA shorts, Spats (optional), mouthguard (recommended)'
              },
              {
                label: 'MMA',
                trial: 'Tight-fitting wear; no zippers/velcro',
                member: '7 oz MMA gloves, Rashguard, MMA shorts, Spats (optional), mouthguard'
              },

              
            ].map((style, i) => (
              <Card key={i} sx={{ backgroundColor: '#222', p: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="secondary" sx={{ mb: 1 }}>{style.label}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>🧪 Trial Gear</Typography>
                    <Typography>{style.trial}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>👤 Member Gear</Typography>
                    <Typography>{style.member}</Typography>
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Box>
          <Typography sx={{ mt: 3 }}>
            Please avoid apparel from other gyms or teams. Gear available at front desk or Clinch Fight Shop.
          </Typography>
        </SectionCard>
      </ScrollSection>

      <ScrollSection id="hygiene-&-etiquette">
        <SectionCard
          title="Hygiene & Etiquette"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/v1763685124/IMG_4856_dovkri.jpg"
          icon={<HygieneIcon />}
          invert={false}
        >
          <Typography>• Clean clothes and gear are mandatory</Typography>
          <Typography>• Maintain personal hygiene—combat sports involve close contact</Typography>
          <Typography>• Keep nails trimmed</Typography>
          <Typography>• Indoor flip flops off-mat—wear socks if forgotten</Typography>
          <Typography>• Take items home daily</Typography>
          <Typography>• Do not train with contagious skin conditions</Typography>
          <Typography>• Respect teammates and training partners</Typography>
          <Typography>• Stay home if you’re unwell</Typography>
          <Typography>• Leave ego at the door—training is not competition</Typography>
          <Typography>• Help keep the gym clean</Typography>
        </SectionCard>
      </ScrollSection>

      <ScrollSection id="private-training-&-drop-ins">
        <SectionCard
          title="Private Training & Drop-Ins"
          image="https://res.cloudinary.com/dmu7vt5cs/image/upload/c_crop,ar_4:3/v1763686429/IMG_4814_2_x0qe1g.jpg"
          icon={<PersonIcon />}
          invert={true}
        >
          <Typography>1-on-1 Training: <strong>$60/hour</strong></Typography>
          <Typography sx={{ mt: 2 }}>To book a private session, please contact us via email.</Typography>
          <Typography sx={{ mt: 2 }}>We are not offering single-day drop-ins at this time.</Typography>
        </SectionCard>
      </ScrollSection>
    </Container>
  );
};

export default Memberships;
